import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/api";

export default function SubscriptionSuccessPage() {
  const { refreshUser } = useAuth();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [result, setResult] = useState(null);
  const nav = useNavigate();
  const sid = sp.get("session_id");

  useEffect(() => {
    if (!sid) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/subscription/verify/${sid}`);
        if (cancelled) return;
        setResult(data);
        if (data.status === "paid") {
          setStatus("success");
          await refreshUser();
          toast.success("Subscription active!");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (err) {
        logger.error("Verify subscription", err);
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, refreshUser]);

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="subscription-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 dark:text-white mb-2 font-heading">Activating your subscription...</h2>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 dark:text-white mb-2 font-heading">You're subscribed! 🎉</h2>
            <p className="text-zinc-500 mb-4">{result?.credits} credits added. Renews automatically each month.</p>
            <button onClick={() => nav("/dashboard")} className="bg-brand text-white font-semibold px-8 py-3 hover:bg-brand-hover" data-testid="sub-success-dashboard-btn">Go to Dashboard</button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 dark:text-white mb-2 font-heading">Still processing</h2>
            <p className="text-zinc-500 mb-4">Your payment may still complete. Refresh in a moment or check the Subscription page.</p>
            <button onClick={() => nav("/subscription")} className="bg-brand text-white font-semibold px-8 py-3 hover:bg-brand-hover">View Subscription</button>
          </>
        )}
      </div>
    </AppShell>
  );
}
